import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  AssignSupplementTypeModalDocument,
  ContractTaxonomyFragment,
} from 'generated/resources/graphql'
import _ from 'lodash'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import type { SupplementType } from 'modules/payroll_bundle/components/common/helper/worksheet'
import { useBulkImportSupplements } from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import {
  ADD_SUPPLEMENT_TYPE,
  DELETE_SUPPLEMENT_TYPE,
  UPDATE_DATA,
  UPDATE_SUPPLEMENT_TYPE,
} from 'modules/payroll_bundle/lib/constants'
import Supplement from 'modules/payroll_bundle/store/models/Supplement'

import buildSupplementType from './buildSupplementType'

const getUnitsLabel = (type: string) => {
  switch (type) {
    case 'money':
      return i18n.t('handoffs.supplements.units.money')
    case 'time':
      return i18n.t('handoffs.supplements.units.time')
    case 'units':
      return i18n.t('handoffs.supplements.units.units')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

// Shows the legal entities and for each of them allows to select the taxonomy
// to which the supplement type has to be mapped.
const AssignSupplementTypeModal = ({
  supplementType,
}: {
  supplementType?: SupplementType | null
}) => {
  const { supplementDispatch } = useBulkImportSupplements()

  const [assignedTaxonomies, setAssignedTaxonomies] = React.useState(
    supplementType?.legalEntityIdToTaxonomyId || {}
  )
  const [units, setUnits] = React.useState(supplementType?.units || Supplement.units.money)
  const { data } = useQuery(AssignSupplementTypeModalDocument)

  const legalEntities = data?.companies.legalEntities || []

  const taxonomyOptions = (contractTaxonomies: ContractTaxonomyFragment[]) => {
    return contractTaxonomies
      .filter((taxonomy) => !taxonomy.archived)
      .map((taxonomy) => ({
        label: taxonomy.name,
        value: taxonomy.id,
      }))
  }

  const onTaxonomyChange = (legalEntityId: string, taxonomyId: string) => {
    // When "-- None --" is selected, taxonomyId is the empty string
    setAssignedTaxonomies({ ...assignedTaxonomies, [legalEntityId]: taxonomyId })
  }

  const addSupplementType = () => {
    supplementDispatch({
      type: ADD_SUPPLEMENT_TYPE,
      payload: buildSupplementType({
        legalEntities: legalEntities,
        legalEntityIdToTaxonomyId: assignedTaxonomies,
        units: units,
      }),
    })
    onClose()
  }

  const updateSupplementType = () => {
    supplementDispatch({
      type: UPDATE_SUPPLEMENT_TYPE,
      payload: buildSupplementType({
        legalEntities: legalEntities,
        id: supplementType?.id,
        legalEntityIdToTaxonomyId: assignedTaxonomies,
        units: units,
      }),
    })
    onClose()
  }

  const deleteSupplementType = () => {
    if (!supplementType?.id) {
      throw new Error('Supplement type id is missing')
    }

    supplementDispatch({
      type: DELETE_SUPPLEMENT_TYPE,
      payload: {
        id: supplementType?.id,
      },
    })
    onClose()
  }

  const onClose = () => {
    supplementDispatch({
      type: UPDATE_DATA,
      payload: { showAssignTypeModal: false },
    })
  }

  const labelCurrency = (unit: string): string => {
    if (unit === 'money') {
      return legalEntities.map((le) => le.currency).join(', ')
    }

    return getUnitsLabel(unit ?? '')
  }

  const footer = () => {
    // '' corresponds to "-- None --"
    const assignedTaxonomiesCount = _.filter(assignedTaxonomies, (t) => t !== '').length

    if (supplementType?.id) {
      return (
        <>
          <Button onClick={deleteSupplementType} type="secondary">
            {i18n.t('common.remove')}
          </Button>
          <Button
            disabled={assignedTaxonomiesCount === 0}
            onClick={updateSupplementType}
            type="primary"
          >
            {i18n.t('common.save')}
          </Button>
        </>
      )
    }

    return (
      <Button disabled={assignedTaxonomiesCount === 0} onClick={addSupplementType} type="primary">
        {i18n.t('common.add')}
      </Button>
    )
  }

  const title = supplementType?.id
    ? i18n.t('payroll_bundle.import_supplements.upload.edit_data_modal.title')
    : i18n.t('payroll_bundle.import_supplements.upload.add_data_modal.title')

  return (
    <Modal
      title={title}
      description={i18n.t('payroll_bundle.import_supplements.upload.add_data_modal.description')}
      illustration="document-edit"
      onClose={onClose}
      footer={footer()}
    >
      <Box justifyContent="spaceBetween" gap="s24">
        <Box gap="s24" background="grey200" padding="s16" borderRadius={{ all: 'abs008' }}>
          <Text.Header3>
            {i18n.t('payroll_bundle.import_supplements.upload.add_data_modal.title_section')}
          </Text.Header3>
          {legalEntities.map((legalEntity) => (
            <Box
              key={`legal_entity_${legalEntity.id}`}
              aria-label={i18n.t(
                'payroll_bundle.import_supplements.upload.legal_entities_taxonomies_aria_label'
              )}
            >
              <SelectField
                label={i18n.t('payroll_bundle.import_supplements.upload.add_data_modal.label', {
                  legalEntityName: legalEntity.legalName,
                })}
                value={String(assignedTaxonomies[legalEntity.id])}
                options={taxonomyOptions(legalEntity.contractTaxonomies)}
                onChange={(taxonomyId) => onTaxonomyChange(legalEntity.id.toString(), taxonomyId)}
                showOriginalLabelText
                mandatory
                none={i18n.t('common.none')}
              />
            </Box>
          ))}
        </Box>
        <Box gap="s24" background="grey200" padding="s16" borderRadius={{ all: 'abs008' }}>
          <Text.Header3>
            {i18n.t('payroll_bundle.import_supplements.upload.add_data_modal.measurement_section')}
          </Text.Header3>
          <SelectField
            label={i18n.t('payroll_bundle.import_supplements.upload.add_data_modal.unit')}
            options={Object.keys(Supplement.units)
              .filter((u) => u !== Supplement.units.time)
              .map((value) => ({
                value,
                label: labelCurrency(value),
              }))}
            value={units}
            onChange={(value) => setUnits(value)}
            showOriginalLabelText
            mandatory
          />
        </Box>
      </Box>
    </Modal>
  )
}

export default observer(AssignSupplementTypeModal)
